// D3 Bar Chart for BodyOS data visualizations
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createXAxis,
  createYAxis,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  debounce
} from './utils'

export default class BarChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins },
      xLabel: '',
      yLabel: '',
      horizontal: false,
      grouped: false,
      stacked: false,
      showValues: false,
      showLegend: true,
      animate: true,
      responsive: true,
      barPadding: 0.2,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    // For horizontal bar charts, calculate left margin based on longest label
    const margins = { ...this.options.margins }
    if (this.options.horizontal) {
      const maxLabelLength = Math.max(...this.data.map((d) => String(d.x).length))
      // Approximately 7px per character for monospace font at size 10
      const estimatedLabelWidth = maxLabelLength * 7 + 20
      margins.left = Math.max(margins.left, estimatedLabelWidth)
    }

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      margins
    )
    this.svg = svg

    this.tooltip = createTooltip(this.container)

    if (this.options.horizontal) {
      this.renderHorizontal(g, innerWidth, innerHeight)
    } else {
      this.renderVertical(g, innerWidth, innerHeight)
    }

    return this
  }

  renderVertical(g, innerWidth, innerHeight) {
    const isGrouped = this.options.grouped && this.data[0]?.values
    const isStacked = this.options.stacked && this.data[0]?.values

    if (isGrouped || isStacked) {
      this.renderGroupedVertical(g, innerWidth, innerHeight, isStacked)
    } else {
      this.renderSimpleVertical(g, innerWidth, innerHeight)
    }
  }

  renderSimpleVertical(g, innerWidth, innerHeight) {
    const xScale = d3.scaleBand()
      .domain(this.data.map((d) => d.x))
      .range([0, innerWidth])
      .padding(this.options.barPadding)

    const yMax = d3.max(this.data, (d) => d.y)
    const yScale = d3.scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerHeight, 0])
      .nice()

    createXAxis(g, xScale, innerHeight, {
      label: this.options.xLabel,
      rotate: this.options.rotateXLabels ? -45 : 0
    })
    createYAxis(g, yScale, {
      label: this.options.yLabel,
      gridLines: true,
      width: innerWidth
    })

    const bars = g.selectAll('.bar')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xScale(d.x))
      .attr('width', xScale.bandwidth())
      .attr('y', innerHeight)
      .attr('height', 0)
      .attr('fill', (d, i) => d.color || colors.chart[i % colors.chart.length])
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      bars.transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * animationConfig.stagger)
        .ease(d3.easeCubicOut)
        .attr('y', (d) => yScale(d.y))
        .attr('height', (d) => innerHeight - yScale(d.y))
    } else {
      bars
        .attr('y', (d) => yScale(d.y))
        .attr('height', (d) => innerHeight - yScale(d.y))
    }

    if (this.options.showValues) {
      const labels = g.selectAll('.bar-label')
        .data(this.data)
        .enter()
        .append('text')
        .attr('class', 'bar-label')
        .attr('x', (d) => xScale(d.x) + xScale.bandwidth() / 2)
        .attr('y', (d) => yScale(d.y) - 8)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.sm)
        .attr('font-family', fonts.family)
        .attr('opacity', 0)
        .text((d) => (this.options.formatValue ? this.options.formatValue(d.y) : d.y))

      if (this.options.animate) {
        labels.transition()
          .delay(animationConfig.duration)
          .duration(300)
          .attr('opacity', 1)
      } else {
        labels.attr('opacity', 1)
      }
    }

    this.addBarInteractions(bars)
  }

  renderGroupedVertical(g, innerWidth, innerHeight, stacked) {
    const categories = this.data.map((d) => d.category)
    const series = this.data[0].values.map((v) => v.name)

    const x0Scale = d3.scaleBand()
      .domain(categories)
      .range([0, innerWidth])
      .padding(0.2)

    const x1Scale = d3.scaleBand()
      .domain(series)
      .range([0, x0Scale.bandwidth()])
      .padding(0.05)

    let yMax
    if (stacked) {
      yMax = d3.max(this.data, (d) => d3.sum(d.values, (v) => v.value))
    } else {
      yMax = d3.max(this.data, (d) => d3.max(d.values, (v) => v.value))
    }

    const yScale = d3.scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerHeight, 0])
      .nice()

    createXAxis(g, x0Scale, innerHeight, {
      label: this.options.xLabel,
      rotate: this.options.rotateXLabels ? -45 : 0
    })
    createYAxis(g, yScale, {
      label: this.options.yLabel,
      gridLines: true,
      width: innerWidth
    })

    const categoryGroups = g.selectAll('.category-group')
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', 'category-group')
      .attr('transform', (d) => `translate(${x0Scale(d.category)}, 0)`)

    if (stacked) {
      this.renderStackedBars(categoryGroups, yScale, innerHeight, x0Scale.bandwidth())
    } else {
      categoryGroups.selectAll('.bar')
        .data((d) => d.values)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', (d) => x1Scale(d.name))
        .attr('width', x1Scale.bandwidth())
        .attr('y', innerHeight)
        .attr('height', 0)
        .attr('fill', (d, i) => d.color || colors.chart[i % colors.chart.length])
        .attr('stroke', colors.neutral[900])
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * animationConfig.stagger)
        .ease(d3.easeCubicOut)
        .attr('y', (d) => yScale(d.value))
        .attr('height', (d) => innerHeight - yScale(d.value))
    }

    // Legend for grouped/stacked
    if (this.options.showLegend) {
      const legendItems = series.map((name, i) => ({
        label: name,
        color: colors.chart[i % colors.chart.length]
      }))
      createLegend(g, legendItems, { x: 0, y: -25 })
    }

    this.addBarInteractions(categoryGroups.selectAll('.bar'))
  }

  renderStackedBars(categoryGroups, yScale, innerHeight, bandwidth) {
    categoryGroups.each((categoryData, categoryIndex, nodes) => {
      const group = d3.select(nodes[categoryIndex])
      let y0 = innerHeight

      categoryData.values.forEach((d, i) => {
        const barHeight = innerHeight - yScale(d.value)
        y0 -= barHeight

        group.append('rect')
          .attr('class', 'bar')
          .attr('x', 0)
          .attr('width', bandwidth)
          .attr('y', innerHeight)
          .attr('height', 0)
          .attr('fill', d.color || colors.chart[i % colors.chart.length])
          .attr('stroke', colors.neutral[900])
          .attr('stroke-width', 2)
          .style('cursor', 'pointer')
          .datum(d)
          .transition()
          .duration(animationConfig.duration)
          .delay(i * animationConfig.stagger)
          .ease(d3.easeCubicOut)
          .attr('y', y0)
          .attr('height', barHeight)
      })
    })
  }

  renderHorizontal(g, innerWidth, innerHeight) {
    const yScale = d3.scaleBand()
      .domain(this.data.map((d) => d.x))
      .range([0, innerHeight])
      .padding(this.options.barPadding)

    const xMax = d3.max(this.data, (d) => d.y)
    const xScale = d3.scaleLinear()
      .domain([0, xMax * 1.1])
      .range([0, innerWidth])
      .nice()

    // Y axis (categories)
    const yAxis = g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale))

    yAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    yAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    // X axis (values)
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    // Bars
    const bars = g.selectAll('.bar')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', (d) => yScale(d.x))
      .attr('width', 0)
      .attr('height', yScale.bandwidth())
      .attr('fill', (d, i) => d.color || colors.chart[i % colors.chart.length])
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      bars.transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * animationConfig.stagger)
        .ease(d3.easeCubicOut)
        .attr('width', (d) => xScale(d.y))
    } else {
      bars.attr('width', (d) => xScale(d.y))
    }

    if (this.options.showValues) {
      g.selectAll('.bar-label')
        .data(this.data)
        .enter()
        .append('text')
        .attr('class', 'bar-label')
        .attr('x', (d) => xScale(d.y) + 8)
        .attr('y', (d) => yScale(d.x) + yScale.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.sm)
        .attr('font-family', fonts.family)
        .text((d) => (this.options.formatValue ? this.options.formatValue(d.y) : d.y))
    }

    this.addBarInteractions(bars)
  }

  addBarInteractions(bars) {
    bars
      .on('mouseover', (event, d) => {
        d3.select(event.target)
          .transition()
          .duration(200)
          .attr('opacity', 0.8)

        const value = this.options.formatValue ? this.options.formatValue(d.y || d.value) : (d.y || d.value)
        const label = d.x || d.name || d.category
        const content = `<strong>${label}</strong><br/><span style="color: ${colors.accent}">${value}</span>`
        showTooltipHtml(this.tooltip, content, event)
      })
      .on('mouseout', (event) => {
        d3.select(event.target)
          .transition()
          .duration(200)
          .attr('opacity', 1)
        hideTooltip(this.tooltip)
      })
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
